import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { sharedStyles } from "@lib/sharedStyles";

import "@/components/button/BasicButton";
import "@/components/inputs-textarea/BasicInput";

const API_BASE = (window.location.port === '5173') ? 'http://localhost:8002' : '';

function navigate(path: string) {
	const state = {};
	window.history.pushState(state, '', path);
	window.dispatchEvent(new PopStateEvent('popstate'));
}

function getCSRFToken(): string | null {
	const cookies = document.cookie.split(';');
	for (const cookie of cookies) {
		const [name, value] = cookie.trim().split('=');
		if (name === 'csrftoken') return decodeURIComponent(value);
	}
	return null;
}

function csrfHeaders(extra: Record<string, string> = {}): Record<string, string> {
	const headers: Record<string, string> = { ...extra };
	const csrf = getCSRFToken();
	if (csrf) headers['X-CSRFToken'] = csrf;
	return headers;
}

interface MillionaireOption {
	id: string;
	text: string;
	is_correct: boolean;
}

interface MillionaireQuestionRow {
	id: string;
	text: string;
	options: MillionaireOption[];
	dirty?: boolean;
	saving?: boolean;
	error?: string | null;
}

const EMPTY_NEW_Q = { text: '', option1: '', option2: '', option3: '', option4: '' };

@customElement('millionaire-questions-page')
export default class MillionaireQuestionsPage extends LitElement {
	static readonly styles = [
		sharedStyles,
		css`
			:host { display: block; padding: 1.5rem; max-width: 1400px; }

			h2 { margin: 0; font-size: 1.25rem; }

			.toolbar {
				display: flex;
				gap: 1rem;
				align-items: center;
				margin-bottom: 1rem;
			}
			.toolbar--space-between { justify-content: space-between; }

			.badge {
				display: inline-block;
				padding: 0.1rem 0.5rem;
				border-radius: 0.4rem;
				background: #eef;
				color: #225;
				font-size: 0.8rem;
			}

			/* Create form */
			.create-form {
				border: 1px solid #d0d7de;
				border-radius: 0.5rem;
				padding: 1rem;
				margin-bottom: 1.5rem;
				background: #f6f8fa;
			}
			.create-form h3 {
				margin: 0 0 0.75rem;
				font-size: 1rem;
			}
			.create-form .fields {
				display: flex;
				gap: 0.75rem;
				flex-wrap: wrap;
				align-items: flex-end;
			}
			.create-form .field-question { flex: 2; min-width: 16rem; }
			.create-form .field-option  { flex: 1; min-width: 10rem; }
			.create-form .hint {
				font-size: 0.8rem;
				color: #666;
				margin-top: 0.4rem;
			}

			/* Table */
			table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
			th, td { border: 1px solid #d0d7de; padding: 0.4rem 0.5rem; text-align: left; vertical-align: middle; }
			th { background: #f6f8fa; font-weight: 600; }
			tr.saving { opacity: 0.5; pointer-events: none; }
			tr.dirty td { background: #fffbe6; }

			.actions {
				white-space: nowrap;
				display: flex;
				gap: 0.4rem;
				align-items: center;
				flex-wrap: wrap;
			}

			.error { color: #b00020; font-size: 0.85rem; }
			.global-error { margin-bottom: 1rem; }
		`
	];

	@state() private loading = true;
	@state() private error: string | null = null;
	@state() private creating = false;
	@state() private createError: string | null = null;
	@state() private questions: MillionaireQuestionRow[] = [];
	@state() private newQ = { ...EMPTY_NEW_Q };

	connectedCallback(): void {
		super.connectedCallback();
		this.loadAll();
	}

	private async loadAll() {
		this.loading = true;
		this.error = null;
		try {
			const res = await fetch(`${API_BASE}/api/millionaire/questions/`, { credentials: 'include' });
			if (!res.ok) throw new Error(`Laden fehlgeschlagen (${res.status})`);
			const data = await res.json();
			const items = Array.isArray(data) ? data : (data.results ?? []);
			this.questions = items.map((q: any) => ({ ...q, dirty: false, saving: false, error: null }));
		} catch (e: any) {
			this.error = e?.message ?? 'Laden fehlgeschlagen';
		} finally {
			this.loading = false;
		}
	}

	private _getOptionTexts(row: MillionaireQuestionRow): string[] {
		const ordered = [...(row.options ?? [])].sort((a, b) => Number(b.is_correct) - Number(a.is_correct));
		const texts = ordered.map(o => o.text);
		while (texts.length < 4) texts.push('');
		return texts.slice(0, 4);
	}

	private onRowQuestionChange(idx: number, value: string) {
		const copy = [...this.questions];
		copy[idx] = { ...copy[idx], text: value, dirty: true };
		this.questions = copy;
	}

	private onRowOptionChange(idx: number, optIndex: number, value: string) {
		const copy = [...this.questions];
		const row = { ...copy[idx] };
		const optionTexts = this._getOptionTexts(row);
		optionTexts[optIndex] = value;
		row.options = optionTexts.map((text, i) => ({
			id: row.options?.[i]?.id ?? `tmp-${idx}-${i}`,
			text,
			is_correct: i === 0,
		}));
		row.dirty = true;
		copy[idx] = row;
		this.questions = copy;
	}

	private async saveRow(idx: number) {
		const row = this.questions[idx];
		if (!row?.id) return;
		const optionTexts = this._getOptionTexts(row).map(v => v.trim());
		if (!row.text.trim() || optionTexts.some(v => !v)) {
			this._setRowState(idx, false, 'Bitte Frage und alle 4 Optionen ausfuellen.');
			return;
		}

		this._setRowState(idx, true, null);
		try {
			const res = await fetch(`${API_BASE}/api/millionaire/questions/${encodeURIComponent(row.id)}/`, {
				method: 'PATCH',
				headers: csrfHeaders({ 'Content-Type': 'application/json' }),
				body: JSON.stringify({ text: row.text.trim(), option_texts: optionTexts }),
				credentials: 'include',
			});
			if (!res.ok) throw new Error(`Speichern fehlgeschlagen (${res.status})`);
			const updated = await res.json();
			const copy = [...this.questions];
			copy[idx] = { ...updated, dirty: false, saving: false, error: null };
			this.questions = copy;
		} catch (e: any) {
			this._setRowState(idx, false, e?.message ?? 'Speichern fehlgeschlagen');
		}
	}

	private async deleteRow(idx: number) {
		const row = this.questions[idx];
		if (!row?.id) return;
		if (!confirm(`Frage "${row.text}" wirklich loeschen?`)) return;
		this._setRowState(idx, true, null);
		try {
			const res = await fetch(`${API_BASE}/api/millionaire/questions/${encodeURIComponent(row.id)}/`, {
				method: 'DELETE',
				headers: csrfHeaders(),
				credentials: 'include',
			});
			if (!res.ok) throw new Error(`Loeschen fehlgeschlagen (${res.status})`);
			const copy = [...this.questions];
			copy.splice(idx, 1);
			this.questions = copy;
		} catch (e: any) {
			this._setRowState(idx, false, e?.message ?? 'Loeschen fehlgeschlagen');
		}
	}

	private _setRowState(idx: number, saving: boolean, error: string | null) {
		const copy = [...this.questions];
		copy[idx] = { ...copy[idx], saving, error };
		this.questions = copy;
	}

	private onNewInputChange(e: any) {
		const name = e.detail.name as keyof typeof this.newQ;
		this.newQ = { ...this.newQ, [name]: e.detail.value };
	}

	private get canCreate() {
		const n = this.newQ;
		return !!(n.text.trim() && n.option1.trim() && n.option2.trim() && n.option3.trim() && n.option4.trim());
	}

	private async createNew() {
		if (!this.canCreate) return;
		this.creating = true;
		this.createError = null;
		try {
			const res = await fetch(`${API_BASE}/api/millionaire/questions/`, {
				method: 'POST',
				headers: csrfHeaders({ 'Content-Type': 'application/json' }),
				body: JSON.stringify({
					text: this.newQ.text.trim(),
					option_texts: [
						this.newQ.option1.trim(),
						this.newQ.option2.trim(),
						this.newQ.option3.trim(),
						this.newQ.option4.trim(),
					]
				}),
				credentials: 'include',
			});
			if (!res.ok) throw new Error(`Erstellen fehlgeschlagen (${res.status})`);
			const created = await res.json();
			this.questions = [{ ...created, dirty: false, saving: false, error: null }, ...this.questions];
			this.newQ = { ...EMPTY_NEW_Q };
		} catch (e: any) {
			this.createError = e?.message ?? 'Erstellen fehlgeschlagen';
		} finally {
			this.creating = false;
		}
	}

	render() {
		if (this.loading) return html`<div>Lade Fragen…</div>`;

		return html`
			<div class="toolbar toolbar--space-between">
				<div style="display:flex; gap:0.75rem; align-items:center;">
					<h2>Millionaire Fragen</h2>
					<span class="badge">${this.questions.length}</span>
				</div>
				<basic-button @click=${() => navigate('/quiz-start')}>Zurueck zum Hauptmenue</basic-button>
			</div>

			${this.error ? html`<div class="error global-error">${this.error}</div>` : nothing}

			<div class="create-form">
				<h3>Neue Frage erstellen</h3>
				<div class="fields">
					<div class="field-question">
						<basic-input
							.value=${this.newQ.text}
							.label=${'Frage'}
							.name=${'text'}
							.id=${'new-text'}
							.inputEventIdentifier=${'new'}
							@input-change=${(e: any) => this.onNewInputChange(e)}
						></basic-input>
					</div>
					<div class="field-option">
						<basic-input
							.value=${this.newQ.option1}
							.label=${'Option 1 (Richtig)'}
							.name=${'option1'}
							.id=${'new-option1'}
							.inputEventIdentifier=${'new'}
							@input-change=${(e: any) => this.onNewInputChange(e)}
						></basic-input>
					</div>
					<div class="field-option">
						<basic-input
							.value=${this.newQ.option2}
							.label=${'Option 2'}
							.name=${'option2'}
							.id=${'new-option2'}
							.inputEventIdentifier=${'new'}
							@input-change=${(e: any) => this.onNewInputChange(e)}
						></basic-input>
					</div>
					<div class="field-option">
						<basic-input
							.value=${this.newQ.option3}
							.label=${'Option 3'}
							.name=${'option3'}
							.id=${'new-option3'}
							.inputEventIdentifier=${'new'}
							@input-change=${(e: any) => this.onNewInputChange(e)}
						></basic-input>
					</div>
					<div class="field-option">
						<basic-input
							.value=${this.newQ.option4}
							.label=${'Option 4'}
							.name=${'option4'}
							.id=${'new-option4'}
							.inputEventIdentifier=${'new'}
							@input-change=${(e: any) => this.onNewInputChange(e)}
						></basic-input>
					</div>
					<basic-button @click=${this.createNew} ?disabled=${!this.canCreate || this.creating}>
						${this.creating ? 'Erstelle…' : 'Erstellen'}
					</basic-button>
				</div>
				${this.createError ? html`<div class="error" style="margin-top:0.5rem;">${this.createError}</div>` : nothing}
			</div>

			<table>
				<thead>
					<tr>
						<th style="width:28%">Frage</th>
						<th style="width:16%">Option 1 (Richtig)</th>
						<th style="width:15%">Option 2</th>
						<th style="width:15%">Option 3</th>
						<th style="width:15%">Option 4</th>
						<th style="width:11%">Aktionen</th>
					</tr>
				</thead>
				<tbody>
					${this.questions.map((q, idx) => {
						const options = this._getOptionTexts(q);
						return html`
							<tr class=${[q.saving ? 'saving' : '', q.dirty ? 'dirty' : ''].join(' ').trim()}>
								<td>
									<basic-input
										.value=${q.text}
										.label=${''}
										.name=${`q-${idx}-text`}
										.id=${`q-${idx}-text`}
										.inputEventIdentifier=${String(idx)}
										@input-change=${(e: any) => this.onRowQuestionChange(idx, e.detail.value)}
									></basic-input>
								</td>
								<td><basic-input .value=${options[0]} .label=${''} .name=${`q-${idx}-o1`} .id=${`q-${idx}-o1`} .inputEventIdentifier=${String(idx)} @input-change=${(e: any) => this.onRowOptionChange(idx, 0, e.detail.value)}></basic-input></td>
								<td><basic-input .value=${options[1]} .label=${''} .name=${`q-${idx}-o2`} .id=${`q-${idx}-o2`} .inputEventIdentifier=${String(idx)} @input-change=${(e: any) => this.onRowOptionChange(idx, 1, e.detail.value)}></basic-input></td>
								<td><basic-input .value=${options[2]} .label=${''} .name=${`q-${idx}-o3`} .id=${`q-${idx}-o3`} .inputEventIdentifier=${String(idx)} @input-change=${(e: any) => this.onRowOptionChange(idx, 2, e.detail.value)}></basic-input></td>
								<td><basic-input .value=${options[3]} .label=${''} .name=${`q-${idx}-o4`} .id=${`q-${idx}-o4`} .inputEventIdentifier=${String(idx)} @input-change=${(e: any) => this.onRowOptionChange(idx, 3, e.detail.value)}></basic-input></td>
								<td class="actions">
									<basic-button ?disabled=${!q.dirty || q.saving} @click=${() => this.saveRow(idx)}>Speichern</basic-button>
									<basic-button @click=${() => this.deleteRow(idx)}>Loeschen</basic-button>
									${q.error ? html`<div class="error">${q.error}</div>` : nothing}
								</td>
							</tr>
						`;
					})}
				</tbody>
			</table>
		`;
	}
}
